import {AxiosResponse} from 'axios';
import { action, computed, observable, makeObservable } from 'mobx';

type State = 'pending' | 'resolved' | 'rejected';

export interface BaseHttpPromiseState<T> {
  readonly state: State;
  readonly response: AxiosResponse<T> | null;
  readonly value: T | null;
  readonly error: any | null;

  readonly isPending: boolean;
  readonly isCompleted: boolean;
  readonly isResolved: boolean;
  readonly isRejected: boolean;
}

export interface HttpPromisePendingState<T> extends BaseHttpPromiseState<T> {
  readonly state: 'pending';
  readonly response: null;
  readonly value: null;
  readonly error: null;

  readonly isPending: true;
  readonly isResolved: false;
  readonly isRejected: false;
  readonly isCompleted: false;
}

export interface HttpPromiseResolvedState<T> extends BaseHttpPromiseState<T> {
  readonly state: 'resolved';
  readonly response: AxiosResponse<T>;
  readonly value: T;
  readonly error: null;

  readonly isPending: false;
  readonly isCompleted: true;
  readonly isResolved: true;
  readonly isRejected: false;
}

export interface HttpPromiseRejectedState<T> extends BaseHttpPromiseState<T> {
  readonly state: 'rejected';
  readonly response: null;
  readonly value: null;
  readonly error: any;

  readonly isPending: false;
  readonly isCompleted: true;
  readonly isResolved: false;
  readonly isRejected: true;
}

export type HttpPromiseFulfilledState<T> =
  HttpPromiseResolvedState<T>
  | HttpPromiseRejectedState<T>;

export type HttpPromiseState<T> = HttpPromisePendingState<T>
  | HttpPromiseResolvedState<T>
  | HttpPromiseRejectedState<T>;

class HttpPromiseStateClass<T> implements BaseHttpPromiseState<T> {
  @observable state: State;
  @observable.ref response: AxiosResponse<T> | null;
  @observable.ref value: T | null;
  @observable.ref error: any | null;

  constructor(private readonly promise: Promise<AxiosResponse<T>>) {
    makeObservable(this);
    this.state = 'pending';
    this.value = this.response = this.error = null;
    promise.then(this.onResolved, this.onRejected);
  }

  @action
  private onResolved = (response: AxiosResponse<T>) => {
    this.state = 'resolved';
    this.response = response;
    this.value = response.data;
  };

  @action
  private onRejected = (error: any) => {
    this.state = 'rejected';
    this.error = error;
  };

  @computed
  get isCompleted(): boolean {
    return this.state === 'rejected' || this.state === 'resolved';
  };

  @computed
  get isPending(): boolean {
    return this.state === 'pending';
  }

  @computed
  get isResolved(): boolean {
    return this.state === 'resolved';
  }

  @computed
  get isRejected(): boolean {
    return this.state === 'rejected';
  }
}

export function watchHttpPromise<T>(promise: Promise<AxiosResponse<T>>): HttpPromiseState<T> {
  return new HttpPromiseStateClass(promise) as HttpPromiseState<T>;
}
